<template>
  <section>
    <div
      v-if="stepTransitionLoading"
      class="enter-termination-loading-overlay">
      <coozzy-spinner />
    </div>
    <div>
      <h1
        v-if="!loading">
        {{ $t('message.onBoarding.buildings.objects.tenancies.termination') }}
      </h1>
    </div>
    <div class="row">
      <div class="col">
        <form-wizard
          id="wizard"
          :hide-buttons="loading || loadingCreateDocuments || stepTransitionLoading"
          title=""
          subtitle=""
          step-size="xs"
          color="rgb(0, 68, 95)"
          @on-change="handleTabChange">
          <template #title>
            <span />
          </template>
          <tab-content :title="$t('message.enterTermination.otherTenancies')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <enter-termination-other-tenancies
              v-else-if="!loading && objectsWithSameTenants.length > 0 && sameTenanciesByObject.length > 0"
              :objects-with-same-tenants="objectsWithSameTenants"
              :same-tenancies-by-object="sameTenanciesByObject"
              :object-id="objectId"
              @selected-object="selectedObject" />
          </tab-content>
          <tab-content :title="$t('message.enterTermination.reviewTermination')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <!-- Lazy: form-wizard uses v-show; only mount once this step is reached -->
            <enter-termination-review
              v-else-if="!loading && selectedObjectToProcess.length > 0 && maxMountedTab >= 1"
              :selected-object-to-process="selectedObjectToProcess"
              :same-tenancies-by-object="sameTenanciesByObject"
              :tenancy-id="tenancyId"
              @selected-entries-review="itemSelectedReview" />
          </tab-content>
          <tab-content :title="$t('message.enterTermination.terminationDetails')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <div v-else-if="!loading && selectedObjectToProcess.length > 0 && lastTenancy && maxMountedTab >= 2">
              <enter-termination-details
                ref="enter-termination-details"
                :selected-object-to-process="selectedObjectToProcess"
                :same-tenancies-by-object="sameTenanciesByObject"
                :tenancy-id="tenancyId"
                :array-details-of-tenants="detailsOfTenants"
                @tenancy-details-updated="tenancyDetailsUpdated"
                @object-marketing-note-updated="objectMarketingNoteUpdated" />
            </div>
          </tab-content>
          <tab-content :title="$t('message.enterTermination.createDocument')">
            <div
              v-if="loading || loadingCreateDocuments"
              class="text-center">
              <coozzy-spinner />
            </div>
            <div v-else-if="!loading && !loadingCreateDocuments && maxMountedTab >= 3">
              <enter-termination-create-document
                :selected-object-to-process="selectedObjectToProcess"
                :is-portal-link-enabled="isPortalLinkEnabled"
                @attach-tenancy="addDocumentToTenancy"
                @show-in-portal="showInPortalMethod"
                @selected-contract="contractSelectedAction" />
            </div>
          </tab-content>
          <tab-content
            :title="$t('message.enterTermination.addVacancy')">
            <div
              v-if="loading"
              class="text-center">
              <coozzy-spinner />
            </div>
            <enter-termination-add-tenancy
              v-if="!loading && selectedObjectToProcess.length > 0 && maxMountedTab >= 4"
              ref="enter-termination-add-tenancy"
              :selected-object-to-process="selectedObjectToProcess"
              :details-of-termination="detailsOfTenants"
              @vacancies-ready="onVacanciesReady"
              @all-vacancy-added="allVacancyAdded"
              @object-marketing-note-updated="objectMarketingNoteUpdated" />
          </tab-content>
          <template
            #footer="props">
            <template v-if="!loading && !loadingCreateDocuments && !stepTransitionLoading">
              <div class="wizard-footer-left">
                <wizard-button
                  v-if="props.activeTabIndex === 0"
                  :style="props.fillButtonStyle"
                  @click.native="$router.back()">
                  {{ $t('message.generic.cancel') }}
                </wizard-button>
                <wizard-button
                  v-else
                  :style="props.fillButtonStyle"
                  @click.native="backClicked(props)">
                  {{ $t('message.generic.back') }}
                </wizard-button>
              </div>
              <div class="wizard-footer-right">
                <wizard-button
                  v-if="!props.isLastStep && props.activeTabIndex !== 2 && !(props.activeTabIndex === 3 && anyContract)"
                  :disabled="contactIds.length !== 0"
                  class="wizard-footer-right"
                  :style="props.fillButtonStyle"
                  @click.native="nextStep(props)">
                  {{ $t('message.generic.next') }}
                </wizard-button>
                <wizard-button
                  v-else-if="props.activeTabIndex === 2"
                  :disabled="contactIds.length !== 0"
                  class="wizard-footer-right"
                  :style="props.fillButtonStyle"
                  @click.native="setTermination(props)">
                  {{ $t('message.enterTermination.saveAndNext') }}
                </wizard-button>
                <wizard-button
                  v-else-if="props.activeTabIndex === 3 && anyContract"
                  :disabled="contactIds.length !== 0"
                  class="wizard-footer-right"
                  :style="props.fillButtonStyle"
                  @click.native="createDocumentAndNextStep(props)">
                  {{ $t('message.enterTermination.createDocument') }}
                </wizard-button>
                <template v-else>
                  <wizard-button
                    class="wizard-footer-right"
                    :style="props.fillButtonStyle"
                    :disabled="savingVacancy || !vacancyFormReady"
                    @click.native="addVacancyInTermination(false)">
                    <template>
                      {{ $t('message.generic.form.save') }}
                    </template>
                  </wizard-button>
                  <wizard-button
                    v-if="isAdminModule || isAccountingModule"
                    class="wizard-footer-right mr-2"
                    :style="props.fillButtonStyle"
                    :disabled="savingVacancy || !vacancyFormReady"
                    @click.native="addVacancyInTermination(true)">
                    <template>
                      {{ $t('message.generic.form.saveAndMarkForMarketing') }}
                    </template>
                  </wizard-button>
                </template>
              </div>
            </template>
            <b-modal
              ref="confirmation-modal-review-contacts"
              no-close-on-backdrop
              hide-footer
              :title="$t('message.enterTermination.noAllTerminationReceived.title')">
              <div class="col pl-0">
                <p>{{ $t('message.enterTermination.noAllTerminationReceived.text') }}</p>
              </div>
              <div class="col pl-0">
                <coozzy-button
                  size="small"
                  class="mb-0 border"
                  design="transparent"
                  @click="hideModal">
                  {{ $t('message.generic.cancel') }}
                </coozzy-button>
                <coozzy-button
                  size="small"
                  class="float-right mb-0"
                  design="green"
                  @click="confirmReviewContacts(props)">
                  {{ $t('message.enterTermination.noAllTerminationReceived.continue') }}
                </coozzy-button>
              </div>
            </b-modal>
          </template>
        </form-wizard>
      </div>
    </div><b-modal
      id="confirmationModal"
      ref="confirmationModal"
      no-close-on-backdrop
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer>
      <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
      <div class="col">
        <coozzy-button
          size="small"
          class="border mb-0"
          design="transparent"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          @click="confirmCancellation">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>

<script>
import Vue from 'vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import ContactApi from '@/misc/apis/ContactApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { onboarding } from '@/mixins/onboarding'
import { routeChecks } from '@/mixins/routeChecks'
import EnterTerminationAddTenancy from '@/enterTermination/EnterTerminationAddTenancy'
import EnterTerminationCreateDocument from '@/enterTermination/EnterTerminationCreateDocument'
import EnterTerminationDetails from '@/enterTermination/EnterTerminationDetails'
import EnterTerminationOtherTenancies from '@/enterTermination/EnterTerminationOtherTenancies'
import EnterTerminationReview from '@/enterTermination/EnterTerminationReview'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { user } from '@/mixins/user'
import { isActiveTenancyStatus, resolveLastTenancy } from '@/misc/utils/tenancyUtils'

export default {
  name: 'EnterTerminationView',
  components: {
    CoozzyButton,
    CoozzySpinner,
    EnterTerminationAddTenancy,
    EnterTerminationCreateDocument,
    EnterTerminationDetails,
    EnterTerminationOtherTenancies,
    EnterTerminationReview
  },
  mixins: [formatting, obj, onboarding, routeChecks, user],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },
  beforeRouteLeave (to, from, next) {
    this.nextToPath = to
    if (!this.confirmCancellationProcess) {
      this.$refs.confirmationModal.show()
    } else {
      next()
    }
  },
  props: {
    objectId: {
      type: String,
      required: true
    },
    tenancyId: {
      type: [String, Number],
      default: ''
    },
    buildingId: {
      type: [String, Number],
      default: ''
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.onBoarding.buildings.objects.tenancies.termination')
    }
  },
  data() {
    return {
      anyContract: false,
      addDocumentToContact: false,
      contactIds: [],
      detailsOfTenants: [],
      lastTenancy: null,
      loading: false,
      loadingCreateDocuments: false,
      object: null,
      objectsWithSameTenants: [],
      sameTenanciesByObject: [],
      selectedEntriesContract: [],
      selectedEntriesReview: [],
      selectedObjectToProcess: [],
      preventChangesInSelectedObjectToProcess: false,
      tenancy: null,
      visibleModal: false,
      confirmationReviewContacts: false,
      confirmCancellationProcess: false,
      nextToPath: null,
      showInPortal: false,
      markForMarketing: false,
      savingVacancy: false,
      vacancyFormReady: false,
      // form-wizard tabs use v-show; gate heavy steps so they mount only after first visit
      maxMountedTab: 0,
      stepTransitionLoading: false
    }
  },
  computed: {
    isPortalLinkEnabled() {
      return this.$store.getters['onboarding/getIsPortalLinkEnabled']
    },
    onlyOneTenant() {
      return this.sameTenanciesByObject.map(x => x.tenant.contactIds.length === 1).every(Boolean)
    },
    prevRoutePath() {
      return this.prevRoute ? this.prevRoute.path : '/'
    },
    prevRouteQuery() {
      return this.prevRoute ? this.prevRoute.query : null
    }
  },
  mounted() {
    this.loading = true
    ObjectApi.getObject(this.objectId).then(values => {
      this.object = values.object
      return ObjectApi.getTenancies([this.object.id])
    }).then(response => {
      if (!response || response.tenancies.length === 0) {
        return
      }
      this.lastTenancy = resolveLastTenancy(response.tenancies, this.tenancyId)
      if (!this.lastTenancy) {
        return
      }

      const contactId = this.lastTenancy.tenant?.contactIds?.[0]
      if (!contactId) {
        this.seedCurrentObjectAndTenancy()
        return
      }

      return ObjectApi.getTenanciesByTenant(this.lastTenancy.ownerId, contactId).then((tenantResponse) => {
        for (const tenancy of tenantResponse.tenancies || []) {
          if (tenancy?.objectId && isActiveTenancyStatus(tenancy.status)) {
            this.sameTenanciesByObject.push(tenancy)
          }
        }
        this.seedCurrentTenancy()
        const objectIds = this.sameTenanciesByObject.map(x => x.objectId).filter(Boolean)
        if (objectIds.length === 0) {
          this.seedCurrentObjectAndTenancy()
          return
        }
        return ObjectApi.listObjectsByIds(objectIds).then((objectsResponse) => {
          for (const obj of objectsResponse.objects || []) {
            if (obj?.id && !this.objectsWithSameTenants.some(o => o?.id === obj.id)) {
              this.objectsWithSameTenants.push(obj)
            }
          }
          this.seedCurrentObjectAndTenancy()
        })
      })
    }).catch(e => {
      console.log(e)
      Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
      this.$router.back()
    }).finally(() => {
      this.loading = false
    })
  },
  methods: {
    seedCurrentTenancy() {
      if (this.lastTenancy?.id && !this.sameTenanciesByObject.some(t => t?.id === this.lastTenancy.id)) {
        this.sameTenanciesByObject.push(this.lastTenancy)
      }
    },
    seedCurrentObjectAndTenancy() {
      this.seedCurrentTenancy()
      if (this.object?.id && !this.objectsWithSameTenants.some(o => o?.id === this.object.id)) {
        this.objectsWithSameTenants.push(this.object)
      }
      // Skip waiting for the objects table; avoid adding undefined if current unit isn't found.
      if (this.object?.id && this.selectedObjectToProcess.length === 0) {
        this.selectedObjectToProcess = [this.object]
      }
    },
    confirmCancellation() {
      this.confirmCancellationProcess = true
      if (this.nextToPath !== null) {
        this.$router.push({ name: this.nextToPath.name, params: this.nextToPath.params, query: this.nextToPath.query }, () => {
        })
      } else {
        this.$router.push({ path: this.prevRoutePath, query: this.prevRouteQuery }, () => {
        })
      }
    },
    handleTabChange(prevIndex, nextIndex) {
      if (nextIndex === 0) {
        // Unmount later steps so selection changes on step 1 do not rebuild ObjectTenancy / vacancy forms
        this.maxMountedTab = 0
        this.vacancyFormReady = false
        this.stepTransitionLoading = false
        this.preventChangesInSelectedObjectToProcess = false
      } else {
        this.maxMountedTab = Math.max(this.maxMountedTab, nextIndex)
        this.preventChangesInSelectedObjectToProcess = true
      }
    },
    async yieldForPaint() {
      await this.$nextTick()
      await new Promise(resolve => {
        window.setTimeout(() => resolve(), 50)
      })
    },
    async advanceWithLoading(props, advanceFn) {
      this.stepTransitionLoading = true
      await this.yieldForPaint()
      advanceFn()
      this.scrollToTop()
      await this.$nextTick()
      await this.yieldForPaint()
      this.stepTransitionLoading = false
    },
    async nextStep(props) {
      if (this.stepTransitionLoading) {
        return
      }
      // Leaving object selection mounts review (and optionally details when onlyOneTenant)
      if (props.activeTabIndex === 0) {
        await this.advanceWithLoading(props, () => {
          props.nextTab()
          if (this.onlyOneTenant) {
            props.nextTab()
          }
        })
        return
      }
      if (props.activeTabIndex === 1) {
        if (this.confirmationReviewContacts === false) {
          this.showModal()
          return
        }
        // Leaving review mounts termination details (ObjectTenancy × N)
        await this.advanceWithLoading(props, () => {
          props.nextTab()
        })
        return
      }
      props.nextTab()
      this.scrollToTop()
    },
    setTermination(props) {
      this.loading = true
      for (let index = 0; index < this.$refs['enter-termination-details'].$refs['enter-termination-details-tenant-items'].length; index++) {
        if (this.$refs['enter-termination-details'].$refs['enter-termination-details-tenant-items'][index].isInvalid()) {
          this.loading = false
          return 0
        }
      }
      const promises = []
      for (let index = 0; index < this.detailsOfTenants.length; index++) {
        const termination = {
          terminateAt: this.detailsOfTenants[index].terminateAt ? this.detailsOfTenants[index].terminateAt : null,
          terminatedOn: this.detailsOfTenants[index].terminatedOn ? this.detailsOfTenants[index].terminatedOn : null,
          liabilityEndsAt: this.detailsOfTenants[index].liabilityEndsAt,
          nextPossibleMoveInAt: this.detailsOfTenants[index].nextPossibleMoveInAt ? this.detailsOfTenants[index].nextPossibleMoveInAt : null,
          movedOutAt: this.detailsOfTenants[index].movedOutAt,
          terminationCreatedAt: this.detailsOfTenants[index].terminationCreatedAt,
          terminationJustification: this.detailsOfTenants[index].terminationJustification,
          terminatedBy: this.detailsOfTenants[index].terminatedBy,
          terminationReason: this.detailsOfTenants[index].terminationReason,
          terminationType: this.detailsOfTenants[index].terminationType,
          documentIds: this.detailsOfTenants[index].documentIds,
          terminationSentByTenantAt: this.detailsOfTenants[index].terminationSentByTenantAt
        }
        promises.push(ObjectApi.setTermination(this.detailsOfTenants[index].id, termination))
      }
      for (const object of this.selectedObjectToProcess) {
        if (object?.id && object.marketingStatus) {
          promises.push(ObjectApi.updateMarketingInfo(
            object.id,
            object.marketingStatus.marketingNote || '',
            object.marketingStatus.startMarketingAt
          ))
        }
      }
      Promise.all(promises)
        .then(response => {
          ObjectApi.updateTenancy(response[0].tenancy)
            .then(response => {
              this.loading = false
              props.nextTab()
              this.scrollToTop()
            })
        })
        .catch(e => {
          this.loading = false
          Vue.toasted.show(this.$t('message.savingErrors.enterTerminationError'), { type: 'error' })
          console.log(e)
        })
    },
    addVacancyInTermination(markForMarketing = false) {
      if (this.savingVacancy || !this.vacancyFormReady) return
      this.savingVacancy = true
      this.markForMarketing = markForMarketing
      this.$refs['enter-termination-add-tenancy'].validateBeforeSaveAddVacancy()
    },
    onVacanciesReady(ready) {
      this.vacancyFormReady = ready
    },
    allVacancyAdded(value) {
      this.savingVacancy = false
      this.confirmCancellationProcess = true
      const nextView = localStorage.getItem('termination-page')
      const selectedItem = nextView === 'tenants' ? this.lastTenancy.id : this.lastTenancy.objectId
      localStorage.removeItem('termination-page')
      localStorage.setItem('latestCreatedTenancy', nextView === 'tenants' ? this.lastTenancy.id : this.lastTenancy.objectId)
      if (value === false) {
        this.loading = false
      } else {
        this.loading = false
        if (!this.markForMarketing) {
          if (this.isOwnerModule) {
            this.$router.push({
              name: 'OwnerBuildingDetailsView',
              params: { id: this.buildingId ? this.buildingId : this.building.id },
              query: { view: nextView, elementSelected: selectedItem }
            }, () => {
            })
          } else if (this.isAdminModule) {
            this.$router.push({
              name: 'AdminBuildingDetailsView',
              params: { id: this.buildingId ? this.buildingId : this.building.id },
              query: { view: nextView, elementSelected: selectedItem }
            }, () => {
            })
          } else if (this.isAssetModule) {
            this.$router.push({
              name: 'AssetBuildingDetailsView',
              params: { id: this.buildingId ? this.buildingId : this.building.id },
              query: { view: nextView, elementSelected: selectedItem }
            }, () => {
            })
          } else if (this.isAccountingModule) {
            this.$router.push({
              name: 'AccountingBuildingDetailsView',
              params: { id: this.buildingId ? this.buildingId : this.building.id },
              query: { view: nextView, elementSelected: selectedItem }
            }, () => {
            })
          }
        } else {
          // Edit mode only when every selected unit is already in marketing; otherwise
          // siblings that are not yet marked would never be transferred to Marketbase.
          const selectedObjects = this.selectedObjectToProcess.filter(o => o?.id)
          const isInMarketingStatus = selectedObjects.length > 0 &&
            selectedObjects.every(o => this.getMarketingStatus(o) !== 'no')
          this.$router.push({
            name: this.isAdminModule
              ? 'AdminMarkForMarketingView'
              : 'AccountingMarkForMarketingView',
            params: {
              id: this.object.id,
              from: this.isAdminModule
                ? 'adminBuilding'
                : 'acountingBuilding'
            },
            query: {
              editMarketingInfoMode: isInMarketingStatus,
              objectIds: selectedObjects.map(o => o.id).join(',')
            }
          })
        }
      }
    },
    tenancyDetailsUpdated(tenantsDetails) {
      this.detailsOfTenants = tenantsDetails
    },
    objectMarketingNoteUpdated(marketingNote, objectId) {
      const object = this.selectedObjectToProcess.find(o => o?.id === objectId)
      if (!object) {
        return
      }
      if (!object.marketingStatus) {
        this.$set(object, 'marketingStatus', {})
      }
      this.$set(object.marketingStatus, 'marketingNote', marketingNote)
    },
    selectedObject(objects) {
      if (!this.preventChangesInSelectedObjectToProcess) {
        this.selectedObjectToProcess = (objects || []).filter(o => o?.id)
        this.isAllTenantsReviewed()
      }
    },
    itemSelectedReview(selected) {
      this.selectedEntriesReview = selected
      this.isAllTenantsReviewed()
    },
    isAllTenantsReviewed() {
      this.confirmationReviewContacts = true
      const selectedIds = this.selectedObjectToProcess.filter(o => o?.id).map(o => o.id)
      Object.keys(this.selectedEntriesReview).forEach(key => {
        if (selectedIds.includes(key) && !this.selectedEntriesReview[key]) {
          this.confirmationReviewContacts = false
        }
      })
      if (Object.keys(this.selectedEntriesReview).length === 0) {
        this.confirmationReviewContacts = false
      }
    },
    backClicked(props) {
      if (props.activeTabIndex === 2) {
        this.isAllTenantsReviewed()
      }
      props.prevTab()
      this.scrollToTop()
    },
    scrollToTop() {
      this.$nextTick(() => {
        const domRect = this.$el.querySelector('#wizard')
        domRect.scrollIntoView(true)
      })
    },
    addDocumentToTenancy(value) {
      this.addDocumentToContact = value
    },
    showInPortalMethod(value) {
      this.showInPortal = value
    },
    contractSelectedAction(value, contracts, objectId) {
      const item = {}
      item.value = value
      item.contracts = contracts
      item.objectId = objectId
      const index = this.selectedEntriesContract.findIndex(c => c.objectId === objectId)
      if (index !== -1) {
        this.selectedEntriesContract[index] = item
      } else {
        this.selectedEntriesContract.push(item)
      }
      this.isAnyContract()
    },
    isAnyContract() {
      this.anyContract = false
      this.selectedEntriesContract.forEach((entry) => {
        if (entry.value.length > 0) {
          this.anyContract = true
        }
      })
    },
    async createDocumentAndNextStep(props) {
      this.loadingCreateDocuments = true
      const current = this
      const promises = []
      const promises2 = []
      const argumentsForPromises2 = []
      for (let index = 0; index < this.selectedEntriesContract.length; index++) {
        promises.push(ObjectApi.getTenancies([this.selectedEntriesContract[index].objectId]).then(response => {
          if (response.tenancies.length > 0) {
            this.selectedEntriesContract[index].tenancy = response.tenancies.sort(function (a, b) {
              return new Date(current.$options.filters.objectToDateInput(b.endingAt)).getTime() - new Date(current.$options.filters.objectToDateInput(a.endingAt)).getTime()
            })[0]
          }
        }))
      }
      await Promise.all(promises)
        .then(() => {
          for (let index = 0; index < this.selectedEntriesContract.length; index++) {
            for (let index2 = 0; index2 < this.selectedEntriesContract[index].value.length; index2++) {
              const signatories = []
              const contract = this.selectedEntriesContract[index].contracts.find(x => x.id === this.selectedEntriesContract[index].value[index2])
              if (contract && contract.firstSignature !== '') {
                signatories.push(contract.firstSignature)
              }
              if (contract && contract.secondSignature) {
                if (contract && contract.firstSignature === '') {
                  signatories.push('')
                }
                signatories.push(contract.secondSignature)
              }
              promises2.push(ObjectApi.RenderTenancyTemplate(this.selectedEntriesContract[index].value[index2], this.selectedEntriesContract[index].tenancy.id, this.addDocumentToContact, this.showInPortal, '', '', '', '', signatories))
              argumentsForPromises2.push(
                {
                  selected: this.selectedEntriesContract[index].value[index2],
                  contract: this.selectedEntriesContract[index].contracts.find(x => x.id === this.selectedEntriesContract[index].value[index2]),
                  tenancy: this.selectedEntriesContract[index].tenancy
                })
            }
          }
        })
      await Promise.all(promises2)
        .then(async response => {
          const renderId = await Promise.all(response.map(async (element, index) => {
            const contract = argumentsForPromises2[index].contract
            const tenancy = argumentsForPromises2[index].tenancy
            const objectNumber = this.objectsWithSameTenants.find(x => x.id === tenancy.objectId).number
            let downloadName = contract.title + '_' + objectNumber
            if (tenancy.vacancy === false && tenancy.tenant.billingContactId) {
              const billingContact = await ContactApi.contactResolve([tenancy.tenant.billingContactId])
                .then(response => {
                  return response.persons.concat(response.companies).find(x => x.id === tenancy.tenant.billingContactId)
                })
              downloadName = contract.title + '_' + billingContact.name + '_' + objectNumber
            }
            return { renderId: element.renderId + '_' + downloadName + '-/-' + contract.enforcePdf, contract: contract }
          }))
          const currentRenderIds = JSON.parse(localStorage.getItem('renderId') || '[]')
          const mergedRenderIds = Array.isArray(currentRenderIds) ? [...currentRenderIds, ...renderId] : renderId
          localStorage.setItem('renderId', JSON.stringify(mergedRenderIds))
        })
        .catch(e => {
          this.loadingCreateDocuments = false
          console.log(e)
          this.loading = false
          Vue.toasted.show(this.$t('message.applicationForm.creditTrust.certificateOrderError'), { type: 'error' })
        })
      this.loadingCreateDocuments = false
      this.selectedEntriesContract = []
      this.isAnyContract()
      this.nextStep(props)
    },
    confirmReviewContacts(props) {
      this.confirmationReviewContacts = true
      this.hideModal()
      this.nextStep(props)
    },
    showModal() {
      this.visibleModal = true
      this.$refs['confirmation-modal-review-contacts'].show()
    },
    hideModal() {
      this.$refs['confirmation-modal-review-contacts'].hide()
      this.visibleModal = false
    }
  }
}
</script>

<style lang="scss" scoped>
.enter-termination-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
}
</style>
